import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) return;
    setIsMobileMenuOpen(false);
    navigate(`/courses?q=${encodeURIComponent(trimmed)}`);
  };

  const isOnHomeHero = !isScrolled && location.pathname === '/';
  const navLinkBase =
    'font-medium transition-colors';
  const navLinkColor = isOnHomeHero
    ? 'text-slate-100 hover:text-white'
    : 'text-slate-600 hover:text-blue-600';
  const navLinkClass = `${navLinkBase} ${navLinkColor}`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span
              className={`text-2xl font-extrabold ${
                isOnHomeHero ? 'text-white' : 'text-slate-900'
              }`}
            >
              Lumina
            </span>
          </Link>

          {/* Center: Desktop Navigation + Search */}
          <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
            <Link
              to="/"
              className={navLinkClass}
            >
              Home
            </Link>
            <Link
              to="/courses"
              className={navLinkClass}
              data-testid="courses-nav-link"
            >
              Courses
            </Link>
            {user && (
              <Link
                to="/dashboard"
                className={navLinkClass}
                data-testid="dashboard-nav-link"
              >
                My Learning
              </Link>
            )}
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-64 lg:w-80"
              role="search"
            >
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isOnHomeHero ? 'text-slate-300' : 'text-slate-400'
                }`}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search courses..."
                className={`w-full rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  isOnHomeHero
                    ? 'bg-white/10 text-white placeholder:text-slate-300 focus:bg-slate-900/40'
                    : 'bg-slate-100 text-slate-900 placeholder:text-slate-400 focus:bg-white'
                }`}
              />
            </form>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-slate-600 font-medium">{user.name}</span>
                <button
                  onClick={handleLogout}
                  className="btn-secondary text-sm py-3"
                  data-testid="logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="login-nav-button">
                  <button className="btn-secondary text-sm py-3">Login</button>
                </Link>
                <Link to="/register" data-testid="register-nav-button">
                  <button className="btn-primary text-sm py-3">Sign Up</button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              <form onSubmit={handleSearchSubmit} className="relative" role="search">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search courses..."
                  className="w-full rounded-full bg-slate-100 pl-9 pr-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                />
              </form>
              <Link
                to="/courses"
                className="block text-slate-600 hover:text-blue-600 font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Courses
              </Link>
              {user && (
                <Link
                  to="/dashboard"
                  className="block text-slate-600 hover:text-blue-600 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Learning
                </Link>
              )}
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full btn-secondary text-sm"
                >
                  Logout
                </button>
              ) : (
                <>
                  <Link to="/login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <button className="w-full btn-secondary text-sm">Login</button>
                  </Link>
                  <Link to="/register" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <button className="w-full btn-primary text-sm">Sign Up</button>
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;
